<script lang="ts" setup>
    import { setupTags } from '@shared/components/Tags/Tags';
    import { type TagsProvide } from '@shared/components/Tags/TagsTypes';
    import { useRouter } from 'vue-router';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { FeatureService } from '@shared/directives/Feature.service';
    import Loading from '@shared/components/Loading.vue';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const tags = setupTags();
    const router = useRouter();

    provide<TagsProvide>('tags', {
        state: tags.state,
        create: tags.create,
        updateSelected: tags.updateSelected,
        toggle: tags.toggle,
    });

    onMounted(() => {
        if (featureAccess.hasFeature('AdminUtilities')) {
            window.addEventListener('keydown', (ev: KeyboardEvent) => {
                if (ev.ctrlKey && ev.altKey && ev.code === 'KeyD') {
                    ev.preventDefault();
                    toggleAdminTools();
                }
            });
        }
    });

    function toggleAdminTools(val: boolean = !admin_tools) {
        if ((admin_tools = val)) document.addEventListener('mousedown', resourceTextElementFromPoint);
        else document.removeEventListener('mousedown', resourceTextElementFromPoint);

        vm.editor.dev.show.admin_tools = val;
    }

    const { editor, activeJourney, account } = vm,
        state = reactive({
            init: false,
        });

    onMounted(async () => {
        awaitData();

        if (activeJourney.JourneyId && !router.currentRoute.value.params.JourneyId) {
            resetActiveJourney();
        }
    });

    const awaitData = async () => {
        state.init &&= false;

        await new Promise((res: any) => (editor.init = res));

        editor.segmentGraph = new GraphData();
        state.init = true;

        editor.history.cache.journey = deepClone(activeJourney);
    };

    account.listings.add('/tags', { map_key: 'TagId' });

    //** prep for admin utilities
    //** testing markup resource editor to start with
    /** mod mode state */
    let admin_tools = false,
        editing_resource: string | null = null;

    /** contains currently edited resources */
    const editedResources: Indexable<string, string> = {};

    /** from point, check if text node. if value is a resource string add key to dataset & set 'contenteditable' attribute to element */
    const resourceTextElementFromPoint = (ev: any) => {
        ev.stopPropagation();
        // ev.preventDefault()

        let el = document.elementFromPoint(ev.x, ev.y) as HTMLElement;
        if (!el) return;

        if (el.dataset.mod_reskey) editResource(el);
        else if (el.childNodes[0]?.nodeType === Node.TEXT_NODE) getResourceKey(el);
    };

    const getResourceKey = (el: HTMLElement) => {
        let regexp = new RegExp(
            el.innerText
                .replace(/[-\/\\^$*+?.()|\[\]{}]/g, '\\$&') // escape!
                .replace(/:\s?$/g, '$&?'), // account for hardcoded suffix characters
            'mi'
        );
        // $Resources.
        for (let reskey in $Resources) {
            if (regexp.test($Resources[reskey])) {
                el.dataset.mod_reskey = reskey;
                el.contentEditable = 'true';

                editResource(el);
                break;
            }
        }
    };

    const editResource = (el: HTMLElement) => {
        let key = el.dataset.mod_reskey;

        if (!key || editing_resource === key) return;
        editing_resource = key;

        el.classList.add('editable-resource');

        let handleInput: any;
        el.addEventListener(
            'input',
            (handleInput = () => {
                editedResources[key!] = el.innerText.replace(/:\s?$/g, '');
            })
        );

        el.addEventListener(
            'blur',
            () => {
                el.classList.remove('editable-resource');

                el.removeEventListener('input', handleInput);
            },
            { once: true }
        );
    };
</script>

<script lang="ts">
    import { inject, onMounted, provide, reactive } from 'vue';
    import vm, { resetActiveJourney } from './Journeys.viewmodel';

    // components
    import Toast from '@journeys/components/Toast/Toast.vue';

    import GraphData from './components/GraphData';
    import { deepClone } from '@shared/utils';

    import '@shared/styles/align-and-spacing.css';
    import '@shared/styles/app-defaults.css';
    import '@shared/styles/containers.css';
    import '@shared/styles/transitions.css';
    import '@shared/styles/inputs.css';
</script>

<template>
    <Suspense>
        <template #default>
            <keep-alive>
                <router-view />
            </keep-alive>
        </template>
        <template #fallback>
            <Loading :show="true" loadingStyle="progress-bar" :fixed="true"></Loading>
        </template>
    </Suspense>
    <Toast />
</template>

<style lang="less">
    .editor-busy {
        pointer-events: none;
    }

    /** state classes */
    .prevent-select :where(.property-window, .modal-container) {
        user-select: none;
    }

    .prevent-select :where(.scrollbar-container) {
        pointer-events: none;
    }
</style>
